import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Eye, FileText, Pencil } from "lucide-react";

import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { Card } from "@/components/ui/card";
import { formatDisplayDate } from "@/lib/dateFormat";
import { cn } from "@/lib/utils";
import type { Client } from "@/services/clientService";

function buildClientActions(
  client: Client,
  t: TFunction,
  navigate: NavigateFunction,
  invoicingBlocked: boolean
): TableAction[] {
  return [
    {
      label: t("clients.list.invoice", "Create invoice"),
      icon: FileText,
      onClick: () => navigate("/invoices/new", { state: { client } }),
      disabled: invoicingBlocked,
    },
    {
      label: t("clients.list.edit", "Edit"),
      icon: Pencil,
      onClick: () => navigate(`/clients/${client.id}/edit`),
    },
    {
      label: t("clients.list.viewDetails", "View details"),
      icon: Eye,
      onClick: () => navigate(`/clients/${client.id}`),
    },
  ];
}

type ClientMobileCardProps = {
  client: Client;
  invoicingBlocked: boolean;
};

export function ClientMobileCard({
  client,
  invoicingBlocked,
}: ClientMobileCardProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const actions = buildClientActions(client, t, navigate, invoicingBlocked);
  const dateAdded = client.createdAt
    ? formatDisplayDate(client.createdAt, { locale: i18n.language })
    : "—";
  const taxId =
    client.documentType === "EXT"
      ? (client.docRecepExt?.trim() ?? "—")
      : (client.rut ?? "—");

  return (
    <Card
      className={cn(
        "overflow-hidden border-border p-4 text-left transition-colors duration-150",
        "hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/clients/${client.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/clients/${client.id}`);
        }
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-medium text-foreground">{client.legalName}</p>
          <p className="text-sm text-muted-foreground">
            {client.displayName ?? "—"}
          </p>
          <p className="text-sm tabular-nums text-foreground">{taxId}</p>
          <p className="text-xs text-muted-foreground">{dateAdded}</p>
        </div>
        <div
          className="shrink-0"
          data-interactive
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TableActionsMenu actions={actions} />
        </div>
      </div>
    </Card>
  );
}
